import { EMPTY_STRING } from '@rotomeca/utils';
import { HTMLBnumCardTitle } from '../../molecules/card-title/bnum-card-title';
import BnumElementInternal from '../../bnum-element-states';
import { TAG_CARD } from '../../../core/utils/constants/tags';
import { removeButtonRole, setButtonRole } from '../../../core/utils/utils';
import style from './bnum-card.less';
import { HTMLBnumIcon } from '../../atoms/icon/bnum-icon';
import { Nullable } from '@rotomeca/utils';
import { Scheduler } from '../../../core/utils/Scheduler';
import { Data, Define, NO_SETTER } from '../../../core/decorators';
import { CustomDom } from '../../bnum-element';
import { TitleData } from './card.internal/TitleData';
import { ScheduleElementAppend } from './card.internal/ScheduleElementAppend';
import { HTMLBnumFragment } from 'components/atoms';
import {
	CSS_CLASS_BODY,
	CSS_CLASS_LOADING,
	CSS_CLASS_TITLE,
	DATA_TITLE_ICON,
	DATA_TITLE_LINK,
	DATA_TITLE_TEXT,
	EVENT_CLICK,
	EVENT_LOADING,
	ICON_SPINNER,
	SLOT_TITLE,
	STATE_CLICKABLE,
	STATE_LOADING,
} from './card.internal/constants';
import { Observe } from 'core/decorators/observe';
import { Listen } from 'core/decorators/listen';

export { HTMLBnumCardElement };

// #region Global constants
const SYMBOL_RESET = Symbol('reset');
// #endregion Global constants

//#region Template
const TEMPLATE = (
	<HTMLBnumFragment>
		<div class={CSS_CLASS_TITLE}>
			<slot name={SLOT_TITLE}></slot>
		</div>
		<div class={CSS_CLASS_BODY}>
			<slot id="mainslot"></slot>
		</div>
	</HTMLBnumFragment>
);
//#endregion Template
/**
 * Élément HTML représentant une carte personnalisée Bnum.
 *
 * Liste des slots :
 * - title : Contenu du titre de la carte. Si aucun contenu n'est fourni, un titre par défaut sera généré à partir des attributs de données.
 * - (slot par défaut) : Contenu du corps de la carte.
 *
 * Liste des data :
 * - title-icon : Icône du titre de la carte.
 * - title-text : Texte du titre de la carte.
 * - title-link : Lien du titre de la carte.
 *
 * /!\ Les data servent à définir un titre par défaut, si le slot "title" est vide ou pas défini.
 *
 * Liste des attributs :
 * - clickable : Rend la carte cliquable.
 * - loading : Indique si la carte est en état de chargement.
 *
 * Évènements personnalisés :
 * - bnum-card:loading : Déclenché lorsque l'état de chargement de la carte change.
 * - bnum-card:click : Déclenché lorsqu'un clic est effectué sur une carte cliquable.
 *
 * @category Card
 *
 * @structure Cas standard
 * <bnum-card>
 * <span slot="title">Titre de la carte</span>
 * <span>Contenu principal.</span>
 * </bnum-card>
 *
 * @structure Carte cliquable
 * <bnum-card clickable>
 * <span slot="title">Carte cliquable</span>
 * <span>Cliquez n'importe où.</span>
 * </bnum-card>
 *
 * @structure Carte avec titre par défaut (via data-attrs)
 * <bnum-card
 * data-title-text="Titre généré"
 * data-title-icon="info"
 * >
 * <span>Le slot "title" est vide.</span>
 * </bnum-card>
 *
 * @structure Carte avec un chargement
 * <bnum-card loading>
 * <bnum-card-title slot="title" data-icon="info">Titre en cours de chargement...</bnum-card-title>
 * <span>Chargement</span>
 * </bnum-card>
 *
 * @state clickable - Est actif lorsque la carte est cliquable.
 * @state loading - Est actif lorsque la carte est en état de chargement.
 *
 * @slot title - Contenu du titre de la carte. Si aucun contenu n'est fourni, un titre par défaut sera généré.
 * @slot (default) - Contenu du corps de la carte. Masqué si l'état `loading` est actif.
 *
 * @cssvar {block} --bnum-card-display - Définit le type d'affichage du composant.
 * @cssvar {var(--bnum-space-m, 15px)} --bnum-card-padding - Définit le padding interne de la carte.
 * @cssvar {auto} --bnum-card-width - Définit la largeur de la carte.
 * @cssvar {auto} --bnum-card-height - Définit la hauteur de la carte.
 * @cssvar {var(--bnum-color-surface, #f6f6f7)} --bnum-card-background-color - Couleur de fond de la carte.
 * @cssvar {var(--bnum-color-surface-hover, #eaeaea)} --bnum-card-background-color-hover - Couleur de fond au survol.
 * @cssvar {var(--bnum-color-surface-active, #dfdfdf)} --bnum-card-background-color-active - Couleur de fond à l'état actif.
 * @cssvar {pointer} --bnum-card-clickable-cursor - Curseur utilisé lorsque la carte est cliquable.
 * @cssvar {var(--bnum-card-loader-animation-rotate360, var(--bnum-animation-rotate360, rotate360 1s linear infinite))} --bnum-card-loader-animation-rotate360 - Animation appliquée au loader (spinner).
 *
 */
@Define({
	tag: TAG_CARD,
	styles: style,
	template: TEMPLATE,
})
@Observe(STATE_CLICKABLE, STATE_LOADING)
class HTMLBnumCardElement extends BnumElementInternal {
	//#region Private fields
	/**
	 * Élément HTML utilisé pour afficher l'indicateur de chargement (spinner).
	 * @private
	 */
	#_loadingElement: HTMLElement | null = null;

	/**
	 * Planificateur responsable des mises à jour asynchrones du corps de la carte.
	 * @private
	 */
	#_scheduleBody: Nullable<Scheduler<Element | symbol>> = null;

	/**
	 * Planificateur responsable des mises à jour asynchrones du titre de la carte.
	 * @private
	 */
	#_scheduleTitle: Nullable<Scheduler<Element | symbol>> = null;

	/**
	 * Planificateur responsable de l'ajout asynchrone d'éléments au sein de la carte.
	 * @private
	 */
	#_scheduleAppend: Nullable<Scheduler<ScheduleElementAppend>> = null;
	//#endregion Private fields

	//#region Getters/Setters

	/**
	 * Icône du titre récupérée depuis les attributs de données du composant (`data-title-icon`).
	 * @private
	 */
	@Data(DATA_TITLE_ICON, NO_SETTER) private accessor _titleIcon: string =
		EMPTY_STRING;

	/**
	 * Texte du titre récupéré depuis les attributs de données du composant (`data-title-text`).
	 * @private
	 */
	@Data(DATA_TITLE_TEXT, NO_SETTER) private accessor _titleText: string =
		EMPTY_STRING;

	/**
	 * Lien du titre récupéré depuis les attributs de données du composant (`data-title-link`).
	 * @private
	 */
	@Data(DATA_TITLE_LINK, NO_SETTER) private accessor _titleLink: string =
		EMPTY_STRING;

	/**
	 * Retourne les données du titre agrégées sous forme d'un objet `TitleData`.
	 * @returns Un objet encapsulant l'icône, le texte, le lien et la validité du titre.
	 * @private
	 */
	private get _titleData(): TitleData {
		return {
			icon: this._titleIcon,
			text: this._titleText,
			link: this._titleLink,
			has: () => {
				return this._titleText !== null && this._titleText !== undefined;
			},
		};
	}

	/**
	 * Indique si la carte est actuellement en état de chargement.
	 * Si vrai, la carte affiche un spinner, masque le corps et désactive ses évènements pointer.
	 * @returns `true` si la carte charge, sinon `false`.
	 */
	get loading(): boolean {
		return this.hasAttribute(STATE_LOADING);
	}

	/**
	 * Définit l'état de chargement de la carte.
	 * @param value - `true` pour activer le chargement, `false` pour le désactiver.
	 */
	set loading(value: boolean) {
		if (value) {
			this.setAttribute(STATE_LOADING, STATE_LOADING);
		} else {
			this.removeAttribute(STATE_LOADING);
		}
	}

	/**
	 * Indique si la carte est configurée pour être cliquable et interactive.
	 * @returns `true` si la carte est cliquable, sinon `false`.
	 */
	public get clickable(): boolean {
		return this.hasAttribute(STATE_CLICKABLE);
	}

	/**
	 * Définit si la carte doit être cliquable ou non, ajustant son rôle pour l'accessibilité.
	 * @param value - `true` pour la rendre cliquable, `false` sinon.
	 */
	public set clickable(value: boolean) {
		if (value) {
			this.setAttribute(STATE_CLICKABLE, STATE_CLICKABLE);
			setButtonRole(this);
		} else {
			this.removeAttribute(STATE_CLICKABLE);
			removeButtonRole(this);
		}
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	/**
	 * Initialise une nouvelle instance de `HTMLBnumCardElement`.
	 */
	constructor() {
		super();
	}

	/**
	 * Étape de pré-chargement du cycle de vie du composant.
	 * Met en place les écouteurs d'évènements initiaux.
	 * @protected
	 */
	protected _p_preload(): void {
		this.#_listenClick();
	}

	/**
	 * Construit et insère les éléments du DOM interne de la carte.
	 * @param container - Le conteneur cible (Shadow Root ou HTMLElement) où créer le DOM.
	 * @protected
	 */
	protected _p_buildDOM(container: CustomDom): void {
		const titleData = this._titleData;

		if (titleData.has()) {
			HTMLBnumCardTitle.Create(titleData.text || EMPTY_STRING, {
				icon: titleData.icon || null,
				link: titleData.link || null,
			}).appendTo(container.querySelector(`slot[name="${SLOT_TITLE}"]`)!);
		}

		this.#_updateDOM();
	}

	/**
	 * Méthode appelée lors de la modification d'un attribut observé.
	 * @param name - Le nom de l'attribut ayant changé.
	 * @param oldVal - La valeur précédente de l'attribut.
	 * @param newVal - La nouvelle valeur de l'attribut.
	 * @protected
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void {
		if (name === STATE_LOADING) {
			this.trigger(EVENT_LOADING, {
				oldValue: oldVal,
				newValue: newVal,
				caller: this,
			});
		}

		this.#_updateDOM();
	}
	//#endregion Lifecycle

	//#region Private methods
	/**
	 * Synchronise l'affichage du DOM avec l'état interne actuel du composant.
	 * @private
	 */
	#_updateDOM(): void {
		this._p_clearStates();

		if (this.clickable) this._p_addState(STATE_CLICKABLE);

		if (this.loading) {
			this._p_addState(STATE_LOADING);

			if (!this.#_loadingElement) {
				const div = this.shadowRoot!.querySelector<HTMLElement>(
					`.${CSS_CLASS_BODY}`,
				)!;
				div.appendChild(this.#_getLoading());
			}
		}
	}

	/**
	 * Instancie et retourne l'élément HTML servant d'indicateur de chargement (spinner).
	 * @returns Le conteneur HTML du spinner.
	 * @private
	 */
	#_getLoading(): HTMLElement {
		if (!this.#_loadingElement) {
			const loadingDiv = document.createElement('div');
			loadingDiv.classList.add(CSS_CLASS_LOADING);

			const spinner = HTMLBnumIcon.Create(ICON_SPINNER).addClass('loader');
			loadingDiv.appendChild(spinner);

			this.#_loadingElement = loadingDiv;
		}

		return this.#_loadingElement;
	}

	/**
	 * Attache un écouteur sur l'évènement `click` du composant.
	 * @returns La fonction associée à la gestion du clic.
	 * @private
	 */
	@Listen('click')
	#_listenClick() {
		return this.#_handleClick;
	}

	/**
	 * Traite l'évènement de clic sur la carte si celle-ci est cliquable.
	 * @param event - L'évènement déclenché par l'utilisateur.
	 * @private
	 */
	#_handleClick(event: MouseEvent): void {
		if (this.clickable) {
			this.trigger(EVENT_CLICK, { originalEvent: event });
		}
	}

	/**
	 * Demande une mise à jour ou la réinitialisation du titre via le planificateur.
	 * @param element - Le nouvel élément titre ou le symbole indiquant une réinitialisation.
	 * @private
	 */
	#_requestUpdateTitle(element: Element | symbol): void {
		this.#_scheduleTitle ??= new Scheduler<Element | symbol>(el =>
			this.#_updateOrResetTitle(el!),
		);
		this.#_scheduleTitle.schedule(element);
	}

	/**
	 * Oriente le composant vers une mise à jour ou une réinitialisation du titre.
	 * @param element - Le nouvel élément ou le symbole de réinitialisation.
	 * @private
	 */
	#_updateOrResetTitle(element: Element | symbol): void {
		if (element === SYMBOL_RESET) this.#_resetTitle();
		else this.#_updateTitle(element as Element);
	}

	/**
	 * Met à jour le DOM pour remplacer le contenu actuel du slot de titre par le nouvel élément.
	 * @param element - L'élément à insérer dans le slot de titre.
	 * @private
	 */
	#_updateTitle(element: Element): void {
		element.setAttribute('slot', SLOT_TITLE);
		const oldTitles = this.querySelectorAll(`[slot="${SLOT_TITLE}"]`);
		oldTitles.forEach(node => node.remove());
		this.appendChild(element);
	}

	/**
	 * Supprime tous les éléments enfants rattachés au slot de titre.
	 * @private
	 */
	#_resetTitle(): void {
		const nodes = this.querySelectorAll(`[slot="${SLOT_TITLE}"]`);
		nodes.forEach(node => node.remove());
	}

	/**
	 * Demande une mise à jour ou la réinitialisation du corps de la carte via le planificateur.
	 * @param element - Le nouvel élément de corps ou le symbole indiquant une réinitialisation.
	 * @private
	 */
	#_requestUpdateBody(element: Element | symbol): void {
		this.#_scheduleBody ??= new Scheduler<Element | symbol>(el =>
			this.#_updateOrResetBody(el!),
		);
		this.#_scheduleBody.schedule(element);
	}

	/**
	 * Oriente le composant vers une mise à jour ou une réinitialisation du corps.
	 * @param element - Le nouvel élément ou le symbole de réinitialisation.
	 * @private
	 */
	#_updateOrResetBody(element: Element | symbol): void {
		if (element === SYMBOL_RESET) this.#_resetBody();
		else this.#_updateBody(element as Element);
	}

	/**
	 * Met à jour le DOM pour remplacer le contenu du corps de la carte par le nouvel élément.
	 * @param element - L'élément à insérer dans le corps de la carte.
	 * @private
	 */
	#_updateBody(element: Element): void {
		element.removeAttribute('slot');

		const oldBodyNodes = Array.from(this.childNodes).filter(
			node =>
				(node.nodeType === Node.ELEMENT_NODE &&
					(node as Element).getAttribute('slot') !== SLOT_TITLE) ||
				(node.nodeType === Node.TEXT_NODE &&
					node.textContent?.trim() !== EMPTY_STRING),
		);

		oldBodyNodes.forEach(node => node.remove());

		this.appendChild(element);
	}

	/**
	 * Supprime tous les éléments enfants appartenant au corps (hors slot de titre).
	 * @private
	 */
	#_resetBody(): void {
		const nodes = Array.from(this.childNodes).filter(
			node =>
				(node.nodeType === Node.ELEMENT_NODE &&
					(node as Element).getAttribute('slot') !== SLOT_TITLE) ||
				(node.nodeType === Node.TEXT_NODE &&
					node.textContent?.trim() !== EMPTY_STRING),
		);

		nodes.forEach(node => node.remove());
	}

	/**
	 * Demande l'ajout d'un élément supplémentaire dans la carte via le planificateur.
	 * @param appended - Données de l'élément planifié à ajouter.
	 * @private
	 */
	#_requestAppendElement(appended: ScheduleElementAppend): void {
		this.#_scheduleAppend ??= new Scheduler<ScheduleElementAppend>(el =>
			this.#_appendElement(el!),
		);
		this.#_scheduleAppend.schedule(appended);
	}

	/**
	 * Ajoute physiquement un élément planifié au sein de la carte.
	 * @param appended - Données de l'élément contenant l'élément et son slot potentiel.
	 * @private
	 */
	#_appendElement(appended: ScheduleElementAppend): void {
		if (appended.slot) appended.element.setAttribute('slot', appended.slot);
		else appended.element.removeAttribute('slot');

		this.appendChild(appended.element);
	}
	//#endregion Private methods

	//#region Public methods
	/**
	 * Remplace intégralement le contenu du slot "title" par l'élément fourni.
	 * @param element - Le nouvel élément à insérer comme titre.
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	updateTitle(element: Element): HTMLBnumCardElement {
		this.#_requestUpdateTitle(element);
		return this;
	}

	/**
	 * Remplace intégralement le contenu du corps principal (slot par défaut) par l'élément fourni.
	 * @param element - Le nouvel élément à insérer dans le corps.
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	updateBody(element: Element): HTMLBnumCardElement {
		this.#_requestUpdateBody(element);
		return this;
	}

	/**
	 * Supprime tous les éléments actuellement insérés dans le slot "title".
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	public clearTitle(): HTMLBnumCardElement {
		this.#_requestUpdateTitle(SYMBOL_RESET);
		return this;
	}

	/**
	 * Supprime tous les éléments actuellement insérés dans le corps principal (hors slot "title").
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	public clearBody(): HTMLBnumCardElement {
		this.#_requestUpdateBody(SYMBOL_RESET);
		return this;
	}

	/**
	 * Ajoute un nouvel élément au sein du slot "title", préservant les éléments déjà présents.
	 * @param element - L'élément à ajouter au titre.
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	public appendToTitle(element: Element): HTMLBnumCardElement {
		this.#_requestAppendElement(new ScheduleElementAppend(element, SLOT_TITLE));
		return this;
	}

	/**
	 * Ajoute un nouvel élément au sein du corps de la carte, préservant les éléments déjà présents.
	 * @param element - L'élément à ajouter au corps.
	 * @returns L'instance courante pour le chaînage d'appels.
	 */
	public appendToBody(element: Element): HTMLBnumCardElement {
		this.#_requestAppendElement(new ScheduleElementAppend(element));
		return this;
	}
	//#endregion Public methods

	//#region Static properties

	/**
	 * Instancie une nouvelle `HTMLBnumCardElement` initialisée avec les options spécifiées.
	 *
	 * @param options - Objet de configuration de la carte.
	 * @param options.title - Élément pour le titre de la carte (optionnel).
	 * @param options.body - Élément pour le corps de la carte (optionnel).
	 * @param options.clickable - Indique si la carte est cliquable (défaut: `false`).
	 * @param options.loading - Indique si la carte est en état de chargement (défaut: `false`).
	 * @returns Une nouvelle instance configurée de l'élément HTML.
	 */
	public static override Create({
		title = null,
		body = null,
		clickable = false,
		loading = false,
	}: {
		title?: Nullable<HTMLBnumCardTitle>;
		body?: Nullable<Element>;
		clickable?: boolean;
		loading?: boolean;
	} = {}): HTMLBnumCardElement {
		const card = document.createElement(this.TAG) as HTMLBnumCardElement;

		if (title) card.updateTitle(title);
		if (body) card.updateBody(body);

		if (clickable) card.setAttribute(STATE_CLICKABLE, STATE_CLICKABLE);

		if (loading) card.setAttribute(STATE_LOADING, STATE_LOADING);

		return card;
	}
	//#endregion Static properties
}
